import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Chip, LinkButton, Skeleton, StatusChip } from '../../components/ui';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import type { Order } from '../../types/Order';
import { isTableNumber } from '../orders/orderApi';
import { Panel, RemoteBody } from './Panel';
import { liveOrders } from './stats';
import type { Remote } from './useRemote';

const useNow = (): number => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(id);
    }, []);
    return now;
};

export const LiveOrders = ({ remote }: { remote: Remote<Order[]> }) => {
    const { t } = useTranslation('home');
    const now = useNow();
    const { locationId } = useActiveLocation();
    return (
        <Panel
            title={t('live.title')}
            action={
                <LinkButton to="/orders" variant="link">
                    {t('live.viewAll')}
                </LinkButton>
            }
        >
            <RemoteBody
                remote={remote}
                skeleton={
                    <div className="grid gap-3">
                        {[0, 1, 2].map((i) => (
                            <Skeleton key={i} className="h-10 w-full" />
                        ))}
                    </div>
                }
            >
                {(orders) => {
                    const rows = liveOrders(orders);
                    if (rows.length === 0) return <p className="py-6 text-center text-small text-muted">{t('live.empty')}</p>;
                    return (
                        <ul className="divide-y divide-line">
                            {rows.map((o) => {
                                const minutes = Math.max(0, Math.floor((now - new Date(o.createdAt).getTime()) / 60_000));
                                return (
                                    <li key={o.id}>
                                        <Link
                                            to={`/orders/${o.id}`}
                                            className="flex min-h-14 items-center gap-3 rounded-control px-1 hover:bg-hover"
                                        >
                                            <span className="text-heading tabular-nums text-strong">{o.orderNumber}</span>
                                            {locationId === null && o.locationName && <span className="truncate text-small text-muted">{o.locationName}</span>}
                                            {o.tableNumber ? (
                                                <Chip variant="outline">{isTableNumber(o.tableNumber) ? t('live.table', { number: o.tableNumber }) : o.tableNumber}</Chip>
                                            ) : (
                                                <Chip variant="info" icon={ShoppingBagIcon}>
                                                    {t('common:orderType.togo')}
                                                </Chip>
                                            )}
                                            <StatusChip status={o.status} />
                                            <span className="ml-auto shrink-0 whitespace-nowrap text-small tabular-nums text-muted">
                                                {minutes === 0 ? t('live.justNow') : t('live.minutes', { count: minutes })}
                                            </span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    );
                }}
            </RemoteBody>
        </Panel>
    );
};
